import type { HistoricNarRecommendation } from "../../../../../api/services/historicNar/types";
import { IconCellVariant } from "../../../../../components/custom-table/tableCell/cellTypes/IconCell";
import { HistoricNarIngestionState } from "../../../../../enums/historicNarIngestionState";
import type { NarAgentGridOrgTab } from "../../narAgentGrid/hooks/useNarAgentGridOrgTabs";

/** Prefer calc1 over calc2, preserving zero; absent numeric volume renders blank. */
export function readVolume(
  analytics: HistoricNarRecommendation["volume_analytics"],
): string {
  const total = analytics?.calc1 ?? analytics?.calc2;
  return typeof total === "number" ? String(total) : "";
}

/** Render fractional scores as percentages and whole-scale scores as rounded values. */
export function formatScore(score: number | undefined): string {
  if (typeof score !== "number") return "";
  return score <= 1 ? `${Math.round(score * 100)}` : `${Math.round(score)}`;
}

/** Only touchless and agentic states show the automation client tooltip. */
export const showInfoIcon = (status: string | undefined): boolean => {
  const statusToShowInfoIcon = [
    HistoricNarIngestionState.TOUCHLESS,
    HistoricNarIngestionState.AGENTIC,
  ];
  return statusToShowInfoIcon.includes(status as HistoricNarIngestionState);
};

/**
 * Format configured client labels as Touchless and Agentic tooltip sections.
 * Missing client IDs and blank labels are omitted; absent lists render no section.
 * Join populated sections with a bullet, returning an empty string when none resolve.
 */
export const generateDisplayTextWithPrefixLabel = (
  row: HistoricNarRecommendation,
  clientOptions: NarAgentGridOrgTab[],
): string => {
  const getClientLabels = (clientIds: string[]): string[] =>
    clientIds
      .map(
        (clientId) =>
          clientOptions.find((client) => client.id === clientId)?.label,
      )
      .filter((label): label is string => !!label);

  const touchlessClientLabels = getClientLabels(
    row.touchless_enabled_for ?? [],
  );
  const agenticClientLabels = getClientLabels(row.agentic_enabled_for ?? []);
  const sections: string[] = [];

  if (touchlessClientLabels.length > 0) {
    sections.push(`Touchless: ${touchlessClientLabels.join(", ")}`);
  }

  if (agenticClientLabels.length > 0) {
    sections.push(`Agentic: ${agenticClientLabels.join(", ")}`);
  }

  return sections.join(" ● ");
};

/**
 * Select the action glyph from the backend ingestion state.
 * Needs-review rows use the red box arrow; watching (Not Ingested) uses blue.
 * All other states, including unknown or empty values, use the edit pencil.
 */
export const determineWhichIconToShow = (status: string): IconCellVariant => {
  switch (status) {
    case HistoricNarIngestionState.NEEDS_REVIEW:
      return IconCellVariant.BOX_ARROW_IN_DOWN_RIGHT_RED;
    case HistoricNarIngestionState.WATCHING:
      return IconCellVariant.BOX_ARROW_IN_DOWN_RIGHT_BLUE;
    default:
      return IconCellVariant.EDIT_PENCIL;
  }
};
